import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import BethlehemStar from '../components/BethlehemStar';
import WaitlistForm from '../components/WaitlistForm';

const Home = () => {
  return (
    <PageLayout
      title="NorthStar Aurora | Next Generation All-in-One Pixel Design Platform"
      description="Professional pixel light show platform with AI-powered sequencing, visual layout design, and real-time streaming. Design, compose, and play in one unified system."
    >
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          .float-animation {
            animation: float 6s ease-in-out infinite;
          }
        `}
      </style>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center px-6 py-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-12 float-animation">
            <BethlehemStar size={180} />
          </div>
          <h1
            className="text-6xl md:text-7xl font-bold mb-6 leading-tight"
            style={{ color: '#f5f1e8' }}
          >
            The Future of Pixel Light Shows
          </h1>
          <p
            className="text-2xl md:text-3xl mb-12 leading-relaxed"
            style={{ color: '#b8c5d6' }}
          >
            One platform to design, compose, and play. All powered by AI.
          </p>
          <Link
            to="/waitlist"
            className="inline-block px-12 py-4 text-xl font-semibold rounded-lg transition-all hover:scale-105 shadow-lg"
            style={{
              background: 'linear-gradient(135deg, #d4af37 0%, #b8962f 100%)',
              color: '#0a1628'
            }}
          >
            Join the Waitlist
          </Link>
        </div>
      </section>

      {/* Feature Cards */}
      <section className="px-6 py-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {/* AI-Powered Sequencing */}
            <div
              className="p-8 rounded-xl"
              style={{
                background: 'rgba(17, 31, 53, 0.6)',
                border: '1px solid #2d4a6f'
              }}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                style={{ background: 'rgba(212, 175, 55, 0.1)' }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h3
                className="text-2xl font-bold mb-4"
                style={{ color: '#d4af37' }}
              >
                AI-Powered Sequencing
              </h3>
              <p
                className="text-lg leading-relaxed"
                style={{ color: '#b8c5d6' }}
              >
                Upload your music. Watch as AI analyzes every beat, energy shift, and mood change to create perfectly synchronized sequences.
              </p>
            </div>

            {/* Visual Layout Designer */}
            <div
              className="p-8 rounded-xl"
              style={{
                background: 'rgba(17, 31, 53, 0.6)',
                border: '1px solid #2d4a6f'
              }}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                style={{ background: 'rgba(212, 175, 55, 0.1)' }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7"/>
                  <rect x="14" y="3" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/>
                </svg>
              </div>
              <h3
                className="text-2xl font-bold mb-4"
                style={{ color: '#d4af37' }}
              >
                Visual Layout Designer
              </h3>
              <p
                className="text-lg leading-relaxed"
                style={{ color: '#b8c5d6' }}
              >
                Design your pixel layout visually. Place props, map pixels, and see your display come to life before a single light turns on.
              </p>
            </div>

            {/* Professional Output */}
            <div
              className="p-8 rounded-xl"
              style={{
                background: 'rgba(17, 31, 53, 0.6)',
                border: '1px solid #2d4a6f'
              }}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                style={{ background: 'rgba(212, 175, 55, 0.1)' }}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </div>
              <h3
                className="text-2xl font-bold mb-4"
                style={{ color: '#d4af37' }}
              >
                Professional Output
              </h3>
              <p
                className="text-lg leading-relaxed"
                style={{ color: '#b8c5d6' }}
              >
                Generate industry-standard FSEQ files. Stream directly to your controllers via E1.31, DDP, or DMX. Compatible with everything.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="px-6 py-24">
        <div className="max-w-5xl mx-auto">
          <h2
            className="text-5xl font-bold text-center mb-16"
            style={{ color: '#f5f1e8' }}
          >
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-12">
            {/* Step 1 */}
            <div className="text-center">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold"
                style={{
                  background: 'linear-gradient(135deg, #d4af37 0%, #b8962f 100%)',
                  color: '#0a1628'
                }}
              >
                1
              </div>
              <h3
                className="text-2xl font-bold mb-4"
                style={{ color: '#d4af37' }}
              >
                Design
              </h3>
              <p
                className="text-lg leading-relaxed"
                style={{ color: '#b8c5d6' }}
              >
                Build your pixel layout visually. Place props, map channels, and configure your entire display.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold"
                style={{
                  background: 'linear-gradient(135deg, #d4af37 0%, #b8962f 100%)',
                  color: '#0a1628'
                }}
              >
                2
              </div>
              <h3
                className="text-2xl font-bold mb-4"
                style={{ color: '#d4af37' }}
              >
                Compose
              </h3>
              <p
                className="text-lg leading-relaxed"
                style={{ color: '#b8c5d6' }}
              >
                Upload your music and let AI create perfectly timed sequences. Or design effects manually with precision tools.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl font-bold"
                style={{
                  background: 'linear-gradient(135deg, #d4af37 0%, #b8962f 100%)',
                  color: '#0a1628'
                }}
              >
                3
              </div>
              <h3
                className="text-2xl font-bold mb-4"
                style={{ color: '#d4af37' }}
              >
                Play
              </h3>
              <p
                className="text-lg leading-relaxed"
                style={{ color: '#b8c5d6' }}
              >
                Stream directly to your controllers or export FSEQ files. Your show is ready to light up the night.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist CTA */}
      <section className="px-6 py-24">
        <div className="max-w-3xl mx-auto text-center">
          <h2
            className="text-5xl font-bold mb-6"
            style={{ color: '#f5f1e8' }}
          >
            Ready to Transform Your Light Shows?
          </h2>
          <p
            className="text-xl mb-12 leading-relaxed"
            style={{ color: '#b8c5d6' }}
          >
            Join the waitlist to be among the first to experience the future of pixel light design.
          </p>
          <div
            className="p-8 rounded-xl"
            style={{
              background: 'rgba(17, 31, 53, 0.6)',
              border: '1px solid #2d4a6f'
            }}
          >
            <WaitlistForm source="homepage" />
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Home;
